import type { PolicyViewModel } from '@/api/types/PolicyViewModel';

import React from 'react';

import Grid from '@mui/material/Grid';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Link, Divider, Typography, CardContent } from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { InfoBox } from 'src/sections/employer-data/shared/components/info-box';
import {
  formatDate,
  getDefaultAddressByPolicy,
} from 'src/sections/employer-data/shared/utils/policyUtils';

type PolicyInformationProps = {
  comboId: string | undefined;
  coverageId: string | undefined;
  viewPolicy: PolicyViewModel | undefined;
};

export default function PolicyGeneralInformation({
  comboId,
  coverageId,
  viewPolicy,
}: PolicyInformationProps) {
  const showComboTooltip = viewPolicy?.comboName !== viewPolicy?.coverageName;

  return (
    <CardContent sx={{ flexGrow: 1, padding: '0px' }} data-testid="policy-information-card">
      <Box sx={{ padding: '1.5rem' }}>
        <Typography
          variant="h6"
          sx={{ color: 'text.primary' }}
          data-testid="coverage-profile-title"
        >
          Policy information
        </Typography>

        <Grid container spacing={2} columns={16} sx={{ mt: 3 }}>
          <Grid size={{ xs: 16, md: 8 }}>
            <InfoBox
              label="Combo name"
              value={viewPolicy?.comboName || ''}
              tooltip={
                showComboTooltip ? 'Combo name is the name of the controlling coverage.' : undefined
              }
              testId="combo-name-field"
              testIdLabel="combo-name-label"
              testIdValue="combo-name-value"
              sx={{ mt: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 16, md: 8 }}>
            <InfoBox
              label="Combo ID"
              value={comboId ?? ''}
              testId="combo-id-field"
              testIdLabel="combo-id-label"
              testIdValue="combo-id-value"
              sx={{ mt: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 16, md: 8 }}>
            <InfoBox
              label="Coverage name"
              value={viewPolicy?.coverageName || ''}
              tooltip="This is the current name of the employer."
              testId="coverage-name-field"
              testIdLabel="coverage-name-label"
              testIdValue="coverage-name-value"
              sx={{ mt: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 16, md: 8 }}>
            <InfoBox
              label="Coverage ID"
              value={viewPolicy?.coverageId || ''}
              testId="coverage-id-field"
              testIdLabel="coverage-id-label"
              testIdValue="coverage-id-value"
              sx={{ mt: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 16 }}>
            <InfoBox
              label="Address"
              value={getDefaultAddressByPolicy(viewPolicy?.policyAddresses ?? []) ?? ''}
              testId="address-field"
              testIdLabel="address-label"
              testIdValue="address-value"
              sx={{ mt: 0, whiteSpace: 'pre-line' }}
            />
          </Grid>
          <Grid size={{ xs: 16 }}>
            <Divider sx={{ borderStyle: 'dashed' }} />
          </Grid>
          <Grid size={{ xs: 16 }}>
            <InfoBox
              label="Policy number"
              value={viewPolicy?.policyNumber || ''}
              testId="policy-number-field"
              testIdLabel="policy-number-label"
              testIdValue="policy-number-value"
              sx={{ mt: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 16, md: 8 }}>
            <InfoBox
              label="Effective date"
              value={formatDate(viewPolicy?.policyEffectiveDate) || ''}
              testId="effective-date-field"
              testIdLabel="effective-date-label"
              testIdValue="effective-date-value"
              sx={{ mt: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 16, md: 8 }}>
            <InfoBox
              label="Expiration date"
              value={formatDate(viewPolicy?.policyExpirationDate) || ''}
              testId="expiration-date-field"
              testIdLabel="expiration-date-label"
              testIdValue="expiration-date-value"
              sx={{ mt: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 16 }}>
            <Divider sx={{ borderStyle: 'dashed' }} />
          </Grid>
          <Grid size={{ xs: 16, md: 8 }}>
            <InfoBox
              label="Market"
              value={viewPolicy?.planTypeEnumDescription ?? ''}
              testId="market-field"
              testIdLabel="market-label"
              testIdValue="market-value"
              sx={{ mt: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 16, md: 8 }}>
            <InfoBox
              label="Policy type"
              value={viewPolicy?.policyTypeEnumDescription ?? ''}
              testId="policy-type-field"
              testIdLabel="policy-type-label"
              testIdValue="policy-type-value"
              sx={{ mt: 0 }}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ borderStyle: 'dashed', marginTop: '.25rem' }} />

      <Box sx={{ margin: '1rem', marginBottom: '0' }}>
        <Link
          component={RouterLink as React.ElementType}
          to={`/dashboard/employer-data/combo/${comboId}/coverage/${coverageId}/policy/${viewPolicy?.policyId}/policy-details`}
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
          data-testid="see-all-policy-details-button"
        >
          See all policy details
          <ArrowForwardIcon sx={{ marginLeft: '0.5rem' }} />
        </Link>
      </Box>
    </CardContent>
  );
}
